import type React from 'react'
import { useThemeColors } from '@/contexts/ThemeContext'
import { useHover } from '@/hooks/useHover'
import { theme } from '@/lib/theme'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'control' | 'destructive'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  children: React.ReactNode
}

/**
 * Reusable button component with consistent styling across the application.
 * Supports multiple variants, sizes, and loading states.
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  children,
  disabled,
  style,
  ...props
}: ButtonProps) {
  const { isHovered, hoverProps } = useHover()
  const colors = useThemeColors()
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return {
          backgroundColor: colors.primaryAccent,
          border: `1px solid ${colors.secondaryBorder}`,
          color: colors.folderSelectedText,
          fontWeight: 'bold' as const,
        }
      case 'secondary':
        return {
          backgroundColor: colors.secondaryBg,
          border: `1px solid ${colors.headerBorder}`,
          color: colors.primaryText,
        }
      case 'control':
        return {
          background: 'transparent',
          border: 'none',
          color: 'inherit',
        }
      case 'destructive':
        return {
          backgroundColor: colors.errorBg,
          border: `1px solid ${colors.errorBorder}`,
          color: colors.errorText,
        }
    }
  }

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return {
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
          fontSize: theme.fontSize.xs,
        }
      case 'md':
        return {
          padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
          fontSize: theme.fontSize.md,
        }
      case 'lg':
        return {
          padding: `${theme.spacing.md} ${theme.spacing.lg}`,
          fontSize: theme.fontSize.lg,
        }
    }
  }

  const baseStyles: React.CSSProperties = {
    borderRadius: theme.borderRadius.sm,
    fontFamily: theme.fontFamily.inherit,
    cursor: disabled || isLoading ? 'default' : 'pointer',
    transition: theme.transitions.default,
    opacity: disabled || isLoading ? 0.7 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...getVariantStyles(),
    ...getSizeStyles(),
    // Apply hover styles for primary variant
    ...(variant === 'primary' &&
      isHovered &&
      !disabled &&
      !isLoading && {
        backgroundColor: colors.hoverAccent,
      }),
  }

  return (
    <button
      style={{ ...baseStyles, ...style }}
      disabled={disabled || isLoading}
      {...hoverProps}
      {...props}
    >
      {isLoading ? '...' : children}
    </button>
  )
}
